import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CnjSearchListItemDto,
  CreateCnjSearchDto,
  CnjSearchDetailDto,
  CancelCnjSearchDto,
} from '@prometeus/contracts';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { useEventBusStore } from '../stores/event-bus.store';
import { formatCNJ } from '../lib/validations';
import { Breadcrumb } from '../components/Breadcrumb';

export const Route = createFileRoute('/w/$workspaceId/processes/external-search')({
  component: ExternalProcessSearchPage,
});

interface PaginatedSearchesResponse {
  data: CnjSearchListItemDto[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const from = new Date(now);
  from.setMonth(now.getMonth() - 1);
  return {
    dateFrom: toDateInputValue(from),
    dateTo: toDateInputValue(now),
  };
}

function formatDate(value: Date | string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('pt-BR');
}

function formatDateTime(value: Date | string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
}

function formatStatus(search: CnjSearchListItemDto): string {
  if (search.status === 'queued') {
    return search.queuePosition ? `Na fila (#${search.queuePosition})` : 'Na fila';
  }
  if (search.status === 'running' && search.cancelRequestedAt) return 'Cancelando...';
  if (search.status === 'running') return 'Em andamento';
  if (search.status === 'completed') return 'Concluída';
  return 'Falhou';
}

function formatEta(etaSeconds: number | null): string {
  if (etaSeconds === null || etaSeconds <= 0) return '-';
  if (etaSeconds < 60) return `${etaSeconds}s`;
  const minutes = Math.floor(etaSeconds / 60);
  const seconds = etaSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

function ExternalProcessSearchPage() {
  const { workspaceId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addToast = useEventBusStore((state) => state.addToast);
  const [page, setPage] = useState(1);
  const [actionsMenuOpenId, setActionsMenuOpenId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null,
  );

  const DROPDOWN_WIDTH = 192; // w-48 (padrão /clients)

  const { data: workspace } = useQuery<{ name?: string }>({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data?.data ?? response.data;
    },
    enabled: !!workspaceId,
  });

  // Fechar dropdown ao clicar fora (padrão /clients)
  useEffect(() => {
    if (!actionsMenuOpenId) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-external-search-dropdown]')) {
        setActionsMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionsMenuOpenId]);

  // Fechar ao rolar (evita dropdown fixo desalinhado) (padrão /clients)
  useEffect(() => {
    if (!actionsMenuOpenId) return;
    const handleScroll = () => setActionsMenuOpenId(null);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [actionsMenuOpenId]);

  const defaultDates = useMemo(() => getDefaultDateRange(), []);
  const [searchForm, setSearchForm] = useState<CreateCnjSearchDto>({
    numeroProcesso: '',
    numeroOab: '',
    ufOab: '',
    nomeAdvogado: '',
    nomeParte: '',
    dateFrom: defaultDates.dateFrom,
    dateTo: defaultDates.dateTo,
  });

  const searchesQuery = useQuery<PaginatedSearchesResponse, Error>({
    queryKey: ['cnj-searches', workspaceId, page],
    queryFn: async () => {
      const response = await api.get(`/processes/external-searches?page=${page}&limit=15`);
      return response.data.data || response.data;
    },
    refetchInterval: (query) => {
      const rows = (query.state.data as PaginatedSearchesResponse | undefined)?.data || [];
      const hasActive = rows.some((row) => row.status === 'queued' || row.status === 'running');
      return hasActive ? 3000 : false;
    },
  });

  const createSearchMutation = useMutation<
    CnjSearchDetailDto,
    Error,
    CreateCnjSearchDto
  >({
    mutationFn: async (payload) => {
      const response = await api.post('/processes/external-searches', payload);
      return response.data.data || response.data;
    },
    onSuccess: (search) => {
      addToast('Pesquisa criada e enfileirada com sucesso.', 'success');
      queryClient.invalidateQueries({ queryKey: ['cnj-searches', workspaceId] });
      navigate({
        to: '/w/$workspaceId/processes/external-search-details',
        params: { workspaceId },
        search: { searchId: search.id },
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || error.message || 'Erro ao criar pesquisa';
      addToast(Array.isArray(message) ? message.join(', ') : message, 'error');
    },
  });

  const deleteSearchMutation = useMutation<void, Error, string>({
    mutationFn: async (searchId) => {
      await api.delete(`/processes/external-searches/${searchId}`);
    },
    onSuccess: () => {
      addToast('Pesquisa excluída com sucesso.', 'success');
      queryClient.invalidateQueries({ queryKey: ['cnj-searches', workspaceId] });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || error.message || 'Erro ao excluir pesquisa';
      addToast(Array.isArray(message) ? message.join(', ') : message, 'error');
    },
  });

  const cancelSearchMutation = useMutation<
    { success: true; removedQueued: number; mode: 'running' | 'queued' },
    Error,
    { searchId: string; payload: CancelCnjSearchDto }
  >({
    mutationFn: async ({ searchId, payload }) => {
      const response = await api.post(`/processes/external-searches/${searchId}/cancel`, payload);
      return response.data.data || response.data;
    },
    onSuccess: (data) => {
      if (data.removedQueued > 0) {
        addToast(
          `Cancelamento solicitado. ${data.removedQueued} pesquisa(s) da fila foram removidas.`,
          'success',
        );
      } else {
        addToast('Cancelamento solicitado com sucesso.', 'success');
      }
      queryClient.invalidateQueries({ queryKey: ['cnj-searches', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['cnj-search-detail', workspaceId] });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || error.message || 'Erro ao cancelar pesquisa';
      addToast(Array.isArray(message) ? message.join(', ') : message, 'error');
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchForm.dateFrom || !searchForm.dateTo) {
      addToast('Informe a data inicial e final.', 'warning');
      return;
    }

    const hasSearchField =
      !!searchForm.numeroProcesso?.trim() ||
      !!searchForm.numeroOab?.trim() ||
      !!searchForm.nomeAdvogado?.trim() ||
      !!searchForm.nomeParte?.trim();

    if (!hasSearchField) {
      addToast('Preencha pelo menos um campo de busca.', 'warning');
      return;
    }

    if (searchForm.numeroOab?.trim() && !searchForm.ufOab?.trim()) {
      addToast('UF OAB é obrigatória quando número OAB é informado.', 'warning');
      return;
    }

    if (new Date(searchForm.dateFrom) > new Date(searchForm.dateTo)) {
      addToast('A data inicial não pode ser maior que a data final.', 'warning');
      return;
    }

    createSearchMutation.mutate({
      ...searchForm,
      numeroProcesso: searchForm.numeroProcesso?.trim() || undefined,
      numeroOab: searchForm.numeroOab?.trim() || undefined,
      ufOab: searchForm.ufOab?.trim() || undefined,
      nomeAdvogado: searchForm.nomeAdvogado?.trim() || undefined,
      nomeParte: searchForm.nomeParte?.trim() || undefined,
    });
  };

  const handleClear = () => {
    setSearchForm({
      numeroProcesso: '',
      numeroOab: '',
      ufOab: '',
      nomeAdvogado: '',
      nomeParte: '',
      dateFrom: defaultDates.dateFrom,
      dateTo: defaultDates.dateTo,
    });
  };

  const searches = searchesQuery.data?.data || [];
  const totalPages = searchesQuery.data?.totalPages || 1;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb
          items={[
            {
              label: workspace?.name ?? 'Workspace',
              to: '/w/$workspaceId/clients',
              params: { workspaceId },
            },
            { label: 'Busca CNJ' },
          ]}
        />

        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-[15px] mb-6"
          style={{ boxShadow: '0 0 15px rgba(0, 0, 0, 0.08)' }}
        >
          <div
            className="flex items-center justify-between p-6 pb-4"
            style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
          >
            <div>
              <h2
                className="text-[16px] text-[#06141B] dark:text-[#F5F6F5]"
                style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}
              >
                Nova pesquisa persistente
              </h2>
              <p
                className="text-gray-500 dark:text-gray-400 mt-1"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
              >
                Preencha pelo menos um critério de busca e o período desejado.
              </p>
            </div>
          </div>

          <div className="p-6 pt-4">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Número do Processo (CNJ)
                  </label>
                  <input
                    type="text"
                    value={searchForm.numeroProcesso || ''}
                    onChange={(e) =>
                      setSearchForm((prev) => ({
                        ...prev,
                        numeroProcesso: formatCNJ(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[10px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Ex: 0846369-27.2020.8.14.0301"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Número OAB
                  </label>
                  <input
                    type="text"
                    value={searchForm.numeroOab || ''}
                    onChange={(e) =>
                      setSearchForm((prev) => ({ ...prev, numeroOab: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[10px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Ex: 123456"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    UF OAB
                  </label>
                  <select
                    value={searchForm.ufOab || ''}
                    onChange={(e) =>
                      setSearchForm((prev) => ({ ...prev, ufOab: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[10px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  >
                    <option value="">Selecione a UF</option>
                    {['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map((uf) => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Nome do Advogado
                  </label>
                  <input
                    type="text"
                    value={searchForm.nomeAdvogado || ''}
                    onChange={(e) =>
                      setSearchForm((prev) => ({ ...prev, nomeAdvogado: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[10px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Nome completo do advogado"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Nome da Parte/Cliente
                  </label>
                  <input
                    type="text"
                    value={searchForm.nomeParte || ''}
                    onChange={(e) =>
                      setSearchForm((prev) => ({ ...prev, nomeParte: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[10px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Nome da parte envolvida"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Data Inicial *
                  </label>
                  <input
                    type="date"
                    required
                    value={searchForm.dateFrom}
                    onChange={(e) =>
                      setSearchForm((prev) => ({ ...prev, dateFrom: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[10px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Data Final *
                  </label>
                  <input
                    type="date"
                    required
                    value={searchForm.dateTo}
                    onChange={(e) =>
                      setSearchForm((prev) => ({ ...prev, dateTo: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[10px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={createSearchMutation.isPending}
                  className="px-4 py-2 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed transition-opacity hover:opacity-90"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#FFFFFF',
                    backgroundColor: '#06141B',
                    border: 'none',
                  }}
                >
                  {createSearchMutation.isPending ? 'Criando pesquisa...' : 'Criar Pesquisa'}
                </button>

                <button
                  type="button"
                  onClick={handleClear}
                  className="px-4 py-2 rounded-[10px] border border-gray-300 dark:border-gray-600 bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#06141B',
                  }}
                >
                  Limpar
                </button>
              </div>
            </form>
          </div>
        </div>

        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-[15px]"
          style={{ boxShadow: '0 0 15px rgba(0, 0, 0, 0.08)' }}
        >
          <div
            className="p-6 pb-4 border-b border-gray-200 dark:border-gray-700"
          >
            <h2
              className="text-[16px] text-[#06141B] dark:text-[#F5F6F5]"
              style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}
            >
              Pesquisas executadas neste workspace
            </h2>
            <p
              className="text-gray-500 dark:text-gray-400 mt-1"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
            >
              Histórico limitado a 15 pesquisas salvas.
            </p>
          </div>

          {searchesQuery.isLoading ? (
            <div className="p-6 text-center text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Carregando histórico...
            </div>
          ) : searches.length === 0 ? (
            <div className="p-6 text-center text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Nenhuma pesquisa executada neste workspace.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto p-6">
                <table className="w-full" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                  <colgroup>
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                        Status
                      </th>
                      <th className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                        Período
                      </th>
                      <th className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                        Solicitante
                      </th>
                      <th className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                        Progresso
                      </th>
                      <th className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                        Totais
                      </th>
                      <th className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                        Criada em
                      </th>
                      <th className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {searches.map((search) => (
                      <tr
                        key={search.id}
                        className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                        onClick={() =>
                          navigate({
                            to: '/w/$workspaceId/processes/external-search-details',
                            params: { workspaceId },
                            search: { searchId: search.id },
                          })
                        }
                      >
                        <td className="py-3 px-3 align-top">
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} className="text-gray-900 dark:text-gray-100">
                            {formatStatus(search)}
                          </div>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px' }} className="text-gray-500 dark:text-gray-400 mt-1">
                            ETA: {formatEta(search.etaSeconds)}
                          </div>
                        </td>
                        <td className="py-3 px-3 align-top">
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} className="text-gray-900 dark:text-gray-100">
                            {formatDate(search.dateFrom)} até {formatDate(search.dateTo)}
                          </div>
                        </td>
                        <td className="py-3 px-3 align-top">
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} className="text-gray-900 dark:text-gray-100">
                            {search.requestedByName}
                          </div>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px' }} className="text-gray-500 dark:text-gray-400">
                            {search.requestedByEmail}
                          </div>
                        </td>
                        <td className="py-3 px-3 align-top">
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} className="text-gray-900 dark:text-gray-100">
                            {search.progress ? `${search.progress.monthsCompleted}/${search.progress.monthsTotal} intervalos` : '-'}
                          </div>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px' }} className="text-gray-500 dark:text-gray-400 mt-1">
                            páginas: {search.progress?.pagesFetched ?? 0} | itens: {search.progress?.itemsFetched ?? 0}
                          </div>
                        </td>
                        <td className="py-3 px-3 align-top">
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} className="text-gray-900 dark:text-gray-100">
                            {search.totalProcesses} processos
                          </div>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px' }} className="text-gray-500 dark:text-gray-400">
                            {search.totalMovements} movimentações
                          </div>
                        </td>
                        <td className="py-3 px-3 align-top">
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} className="text-gray-900 dark:text-gray-100">
                            {formatDateTime(search.createdAt)}
                          </div>
                        </td>
                        <td className="py-3 px-3 align-top">
                          <div className="flex items-center justify-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (actionsMenuOpenId === search.id) {
                                  setActionsMenuOpenId(null);
                                  setDropdownPosition(null);
                                  return;
                                }
                                const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                setDropdownPosition({
                                  top: rect.bottom + 8,
                                  left: rect.left + rect.width / 2 - DROPDOWN_WIDTH / 2,
                                });
                                setActionsMenuOpenId(search.id);
                              }}
                              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                              style={{ color: '#A0AEC0' }}
                              aria-label="Mais ações"
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle cx="10" cy="5" r="1.5" fill="currentColor" />
                                <circle cx="10" cy="10" r="1.5" fill="currentColor" />
                                <circle cx="10" cy="15" r="1.5" fill="currentColor" />
                              </svg>
                            </button>

                            {actionsMenuOpenId === search.id && dropdownPosition
                              ? createPortal(
                                  <div
                                    data-external-search-dropdown
                                    className="fixed w-48 bg-white dark:bg-[#1a1a1a] rounded-lg shadow-lg py-1 border border-gray-200 dark:border-gray-700"
                                    style={{
                                      top: dropdownPosition.top,
                                      left: dropdownPosition.left,
                                      zIndex: 9999,
                                    }}
                                  >
                                    {search.status === 'running' ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            cancelSearchMutation.mutate({
                                              searchId: search.id,
                                              payload: { clearQueued: false },
                                            });
                                            setActionsMenuOpenId(null);
                                          }}
                                          disabled={
                                            cancelSearchMutation.isPending || !!search.cancelRequestedAt
                                          }
                                          className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                          style={{
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: '12px',
                                            fontWeight: 400,
                                          }}
                                        >
                                          {search.cancelRequestedAt ? 'Cancelando...' : 'Cancelar'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            cancelSearchMutation.mutate({
                                              searchId: search.id,
                                              payload: { clearQueued: true },
                                            });
                                            setActionsMenuOpenId(null);
                                          }}
                                          disabled={
                                            cancelSearchMutation.isPending || !!search.cancelRequestedAt
                                          }
                                          className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                          style={{
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: '12px',
                                            fontWeight: 400,
                                          }}
                                        >
                                          Cancelar + limpar fila
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          deleteSearchMutation.mutate(search.id);
                                          setActionsMenuOpenId(null);
                                        }}
                                        disabled={deleteSearchMutation.isPending}
                                        className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                          fontFamily: 'Poppins, sans-serif',
                                          fontSize: '12px',
                                          fontWeight: 400,
                                        }}
                                      >
                                        Excluir
                                      </button>
                                    )}
                                  </div>,
                                  document.body,
                                )
                              : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', backgroundColor: '#F9FAFB' }}
                  >
                    &lt;
                  </button>
                  <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2D3748' }}>
                    Página {page} de {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', backgroundColor: '#F9FAFB' }}
                  >
                    &gt;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
